import { Injectable } from '@angular/core';
import {
	ArchivingConfigs,
	ElsterConfig,
	SchedulingConfig,
	SystemConfig
}                     from '@app/modules/configuration/models/configuration.model';
import {
	BusinessPremisesUsage,
	ErsUsage,
	ErsUsageItem,
	OrganizationUsage,
	TenantUsage,
	Usage
}                     from '@app/modules/usage/models/fiskal-usage.model';

import {
	Address,
	AddressType
}                        from '@app/shared/components/address/models/address.model';
import { ModelTypeEnum } from '@models/transition.model';
import { CountriesEnum } from '@shared/components/country/models/country-country.model';
import { EnumUtils }     from '@utils/helpers';

@Injectable( {
	providedIn: 'root'
} )
export class SanitizerService {

	constructor() { }

	address( address: AddressType, toModelType: ModelTypeEnum ): AddressType {

		let countryCode: string;
		if ( toModelType === ModelTypeEnum.DTO ) {
			countryCode = address?.countryCode?.toUpperCase();
		}
		if ( toModelType === ModelTypeEnum.TMO ) {
			countryCode = address?.countryCode?.toLowerCase();

			const isValidCountryCode: boolean = EnumUtils.getEnumsValuesAsList( CountriesEnum ).includes( countryCode );
			countryCode = isValidCountryCode
										? countryCode
										: null;
		}

		address = {
			additionalData: address?.additionalData ?? null,
			city: address?.city ?? null,
			countryCode,
			houseNumber: address?.houseNumber ?? null,
			postalCode: address?.postalCode ?? null,
			state: address?.state ?? null,
			street: address?.street ?? null
		};

		return address;
	}

	date<T>( date: Date | string, toModelType: ModelTypeEnum, asType?: new() => T ): T {

		if ( !date ) { return null; }

		let instance: T;
		if ( asType ) { instance = new asType(); }

		if ( instance instanceof Date ) { // request for Date return type
			return (
				date instanceof Date ? date : new Date( date )
			) as unknown as T; // convert arg type to return type
		} else { // request for string return type
			return (
				date instanceof Date ? date.toISOString() : date
			) as unknown as T; // convert arg type to return type
		}
	}

	schedulingConfig( entity: SchedulingConfig ): void {
		if ( entity instanceof SchedulingConfig ) {
			Object.assign( entity, {
					days: entity?.days?.length ? entity.days : [],
					startHour: Number.isInteger( entity?.startHour ) ? entity.startHour : null,
					status: entity?.status ? entity.status : null,
					type: entity?.type ? entity.type : null
				}
			);
		}
	}

	// TODO delete when bp/tenant has been adopted to ui's Configurations model
	systemConfig( entity: SystemConfig ): void {

		if ( entity instanceof SystemConfig ) {
			const archivingConfigs: ArchivingConfigs = {
				...new ArchivingConfigs(),
				systemConfigEtag: entity.etag,
				systemConfigs: entity.archiving,
				systemProperties: entity.archivingSystemProperties
			};

			Object.assign( entity, { type: entity.type ?? null, archivingConfigs } );
		}
	}

	elsterConfig( entity: ElsterConfig ): void {

		if ( entity instanceof ElsterConfig ) {
			const elsterConfig: ElsterConfig = {
				...new ElsterConfig(),
				...entity,
				taxationReportingEnabled: entity?.ericEnabled,
				type: entity.type ?? null
			};

			delete elsterConfig.ericEnabled;
			delete entity.ericEnabled;

			Object.assign( entity, { ...elsterConfig } );
		}
	}

	usageData( usage: Usage ): Usage {
		return {
			...usage,
			periodStart: this.date<Date>( usage?.periodStart, ModelTypeEnum.TMO, Date ),
			periodEnd: this.date<Date>( usage?.periodEnd, ModelTypeEnum.TMO, Date )
		};
	}

	tenantUsage( tenantUsageList: TenantUsage[] ): TenantUsage[] {
		if ( !tenantUsageList ) { return tenantUsageList; }

		return tenantUsageList.map( ( tenantUsage: TenantUsage ) => {
			return {
				...tenantUsage,
				billingAddress: this.address( tenantUsage.billingAddress, ModelTypeEnum.TMO ) as Address,
				usage: this.usageData( tenantUsage.usage ),
				organizations: this.organizationUsage( tenantUsage.organizations )
			};
		} );
	}

	organizationUsage( organizationUsageList: OrganizationUsage[] ): OrganizationUsage[] {
		if ( !organizationUsageList ) { return organizationUsageList; }

		return organizationUsageList.map( ( organizationUsage: OrganizationUsage ) => {
			return {
				...organizationUsage,
				addressData: this.address( organizationUsage.addressData, ModelTypeEnum.TMO ) as Address,
				usage: this.usageData( organizationUsage.usage ),
				businessPremises: this.businessPremisesUsage( organizationUsage.businessPremises )
			};
		} );
	}

	businessPremisesUsage( businessPremisesUsageList: BusinessPremisesUsage[] ): BusinessPremisesUsage[] {
		if ( !businessPremisesUsageList ) { return businessPremisesUsageList; }

		return businessPremisesUsageList.map( ( businessPremisesUsage: BusinessPremisesUsage ) => {
			return {
				...businessPremisesUsage,
				activeFrom: this.date<Date>( businessPremisesUsage.activeFrom, ModelTypeEnum.TMO, Date ),
				activeTo: this.date<Date>( businessPremisesUsage.activeTo, ModelTypeEnum.TMO, Date ),
				addressData: this.address( businessPremisesUsage.addressData, ModelTypeEnum.TMO ) as Address,
				usage: this.usageData( businessPremisesUsage.usage ),
				erses: this.ersUsage( businessPremisesUsage.erses )
			};
		} );
	}

	ersUsage( ersUsageList: ErsUsage[] ): ErsUsage[] {
		if ( !ersUsageList ) { return ersUsageList; }

		return ersUsageList.map( ( ersUsage: ErsUsage ) => {
			return {
				...ersUsage,
				usage: this.ersUsageItem( ersUsage.usage )
			};
		} );
	}

	ersUsageItem( ersUsageItemList: ErsUsageItem[] ): ErsUsageItem[] {
		if ( !ersUsageItemList ) { return ersUsageItemList; }

		return ersUsageItemList.map( ( ersUsageItem: ErsUsageItem ) => {
			return {
				...ersUsageItem,
				from: this.date<Date>( ersUsageItem?.from, ModelTypeEnum.TMO, Date ),
				to: this.date<Date>( ersUsageItem?.to, ModelTypeEnum.TMO, Date )
			};
		} );
	}
}
